'use client';

import { Command } from 'cmdk';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { Label } from '../../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { cn } from '../../utils/cn';

export interface UzMultiSelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Optional heading the option is filed under in the list. */
  group?: string;
}

export interface UzMultiSelectProps<TValue extends string = string> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Trigger text when nothing is picked. */
  placeholder?: string;
  searchPlaceholder?: string;
  values: ReadonlyArray<TValue>;
  onValuesChange: (values: TValue[]) => void;
  options: ReadonlyArray<UzMultiSelectOption<TValue>>;
  disabled?: boolean;
  emptyText?: string;
  name?: string;
  containerClassName?: string;
  triggerClassName?: string;
  /** Hide the search box for short lists. Default: shown once >= 8 options. */
  searchable?: boolean;
  /** Offer the "Select all" / "Clear" footer controls. Default true. */
  selectAll?: boolean;
  /** Chips rendered on the trigger before collapsing into "+N more". Default 2. */
  maxChips?: number;
  /** Cap on how many options may be picked at once. */
  maxSelected?: number;
  /** Max height of the scrollable option list. Default `max-h-64`. */
  listClassName?: string;
}

/**
 * Searchable, scrollable MULTI-select. Same shell as `UzSelect` / `UzCombobox`
 * (label, hint, error) but the menu stays open while picking, each row carries a
 * checkbox, and the trigger summarises the picks as chips with per-chip and
 * whole-field clear controls.
 *
 * Selection is a plain array in caller order of clicking - callers that need it
 * stable (URL params, query keys) should sort it themselves.
 *
 * An EMPTY array means "nothing picked"; what that implies (all / none) is the
 * caller's decision, so the placeholder is caller-supplied rather than assumed.
 */
export const UzMultiSelect = <TValue extends string = string>({
  label,
  hint,
  error,
  required,
  placeholder = 'Select options',
  searchPlaceholder = 'Search…',
  values,
  onValuesChange,
  options,
  disabled,
  emptyText = 'No results found',
  name,
  containerClassName,
  triggerClassName,
  searchable,
  selectAll = true,
  maxChips = 2,
  maxSelected,
  listClassName,
}: UzMultiSelectProps<TValue>): React.JSX.Element => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Long lists need the filter; short ones read faster without a box on top.
  const showSearch = searchable ?? options.length >= 8;

  // Reset the filter between openings so a stale query can't hide everything
  // the next time the menu is opened.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selectedSet = useMemo(() => new Set<string>(values), [values]);
  const selectedOptions = useMemo(
    () => options.filter((option) => selectedSet.has(option.value)),
    [options, selectedSet],
  );
  const selectableValues = useMemo(
    () => options.filter((option) => !option.disabled).map((option) => option.value),
    [options],
  );

  const atCap = maxSelected !== undefined && values.length >= maxSelected;

  const toggle = (value: TValue): void => {
    if (selectedSet.has(value)) {
      onValuesChange(values.filter((entry) => entry !== value));
      return;
    }
    if (atCap) return;
    onValuesChange([...values, value]);
  };

  const clearAll = (): void => onValuesChange([]);

  const allSelected =
    selectableValues.length > 0 && selectableValues.every((value) => selectedSet.has(value));

  const chips = selectedOptions.slice(0, Math.max(0, maxChips));
  const overflow = selectedOptions.length - chips.length;

  // Grouped rendering only kicks in when at least one option asks for it.
  const groups = useMemo(() => {
    const order: string[] = [];
    const buckets = new Map<string, UzMultiSelectOption<TValue>[]>();
    for (const option of options) {
      const key = option.group ?? '';
      if (!buckets.has(key)) {
        buckets.set(key, []);
        order.push(key);
      }
      buckets.get(key)!.push(option);
    }
    return order.map((key) => ({ key, options: buckets.get(key)! }));
  }, [options]);

  const renderOption = (option: UzMultiSelectOption<TValue>): React.JSX.Element => {
    const checked = selectedSet.has(option.value);
    const blocked = option.disabled || (!checked && atCap);
    return (
      <Command.Item
        key={option.value}
        value={`${option.label} ${option.value}`}
        disabled={blocked}
        onSelect={() => !blocked && toggle(option.value)}
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
          'aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors',
            checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
          )}
        >
          {checked && <Check className="h-3 w-3" />}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{option.label}</span>
          {option.description && (
            <span className="text-muted-foreground truncate text-xs">{option.description}</span>
          )}
        </span>
      </Command.Item>
    );
  };

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <Label htmlFor={id} className={cn(disabled && 'opacity-60')}>
          {label}
          {required && (
            <span className="text-destructive ml-1 align-middle text-base leading-none font-semibold">
              *
            </span>
          )}
        </Label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            role="combobox"
            name={name}
            aria-expanded={open}
            aria-invalid={!!error || undefined}
            disabled={disabled}
            className={cn(
              'border-input bg-background flex min-h-10 w-full items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
              'focus:border-ring focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus:border-destructive',
              triggerClassName,
            )}
          >
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground truncate">{placeholder}</span>
            ) : (
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                {chips.map((option) => (
                  <span
                    key={option.value}
                    className="bg-muted text-foreground flex max-w-full items-center gap-1 rounded-full py-0.5 pr-1 pl-2 text-xs font-medium"
                  >
                    <span className="truncate">{option.label.trim()}</span>
                    <span
                      aria-label={`Remove ${option.label.trim()}`}
                      // PointerDown + stopPropagation: removes the chip without
                      // nesting a <button> in the trigger or toggling the menu.
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggle(option.value);
                      }}
                      className="text-muted-foreground hover:text-foreground rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </span>
                ))}
                {overflow > 0 && (
                  <span className="text-muted-foreground text-xs font-medium">
                    +{overflow} more
                  </span>
                )}
              </span>
            )}

            <span className="flex shrink-0 items-center gap-1">
              {selectedOptions.length > 0 && !disabled && (
                <span
                  aria-label="Clear selection"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    clearAll();
                  }}
                  className="text-muted-foreground hover:text-foreground rounded-sm p-0.5"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-56 p-0">
          <Command loop>
            {showSearch && (
              <div className="flex items-center border-b px-3">
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder={searchPlaceholder}
                  className="placeholder:text-muted-foreground h-10 w-full bg-transparent text-sm outline-none"
                />
              </div>
            )}

            <Command.List className={cn('overflow-y-auto p-1', listClassName ?? 'max-h-64')}>
              <Command.Empty className="text-muted-foreground py-6 text-center text-sm">
                {emptyText}
              </Command.Empty>
              {groups.map((group) =>
                group.key === '' ? (
                  <React.Fragment key="__ungrouped__">
                    {group.options.map(renderOption)}
                  </React.Fragment>
                ) : (
                  <Command.Group
                    key={group.key}
                    heading={group.key}
                    className="text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold"
                  >
                    {group.options.map(renderOption)}
                  </Command.Group>
                ),
              )}
            </Command.List>

            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
              <span className="text-muted-foreground text-xs">
                {values.length === 0
                  ? 'None selected'
                  : `${values.length} selected${maxSelected ? ` / ${maxSelected}` : ''}`}
              </span>
              <div className="flex items-center gap-2">
                {selectAll && (
                  <button
                    type="button"
                    disabled={allSelected || maxSelected !== undefined}
                    onClick={() => onValuesChange([...selectableValues])}
                    className="text-muted-foreground hover:text-foreground text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Select all
                  </button>
                )}
                <button
                  type="button"
                  disabled={values.length === 0}
                  onClick={clearAll}
                  className="text-muted-foreground hover:text-foreground text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Clear
                </button>
              </div>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
